import React, { useContext, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import LoggedInApps from './LoggedInApps';
import GuestApps from './GuestApps';
import EosApp from './eos/EosApp';
import EGfrApp from './eGfr/EGfrApp';
import QTcCalculator from './QTcCalculator/QTcCalculator';
import Phototherapy from './phototherapy/Phototherapy';
import Bili2022 from './Bili2022/Bili2022';
import Triage from './Triage/Triage';
import Cpr from './Cpr/Cpr';
import CorrectedAge from './CorrectedAge/CorrectedAge';
import TestTubes from './TestTubes/TestTubes';
import PreemieNutrition from './PreemieNutrition/PreemieNutrition';
import NicuNutrition from './NicuNutrition/NicuNutrition';
import NewPreemie from './NewPreemie/NewPreemie';
import SepsisApp from './SepsisApp/SepsisApp';
import HematologicalDiseases from './HematologicalDiseases/HematologicalDiseases';
import CommonLayout from '../components/CommonLayout';
import Asthma from './asthma/Asthma';
import './Apps.scss';
import Antibiogram from './Antibiogram/Antibiogram';
import Modal from './components/Modal';
import InDevelopmentBanner from './components/InDevelopmentBanner';
import type { AppName } from './appNames';

interface Department {
  _id: string;
  nameHebrew: string;
  nameEnglish: string;
  apps: any[];
}

interface Hospital {
  _id: string;
  nameHebrew: string;
  nameEnglish: string;
  departments: Department[];
}

const appMap: Record<AppName, React.ComponentType<any>> = {
  eos: EosApp,
  phototherapy: Phototherapy,
  bili2022: Bili2022,
  'qtc-calculator': QTcCalculator,
  triage: Triage,
  resus: Cpr,
  'resus-nursing': Cpr,
  correctedAge: CorrectedAge,
  'test-tubes': TestTubes,
  egfr: EGfrApp,
  'preemie-nutrition': PreemieNutrition,
  'nicu-nutrition': NicuNutrition,
  antibiogram: Antibiogram,
  asthma: Asthma,
  'new-preemie': NewPreemie,
  'sepsis-screening': SepsisApp,
  'hematological-diseases': HematologicalDiseases,
};

const WELCOME_POPUP_KEY = 'mashlom-main-welcome-shown';

const Apps: React.FC = () => {
  const { authToken, hospitals } = useContext(UserContext) as {
    authToken: string;
    hospitals: Hospital[];
  };

  const { hospitalName, appName } = useParams<{ hospitalName: string; appName?: AppName }>();
  const [searchParams] = useSearchParams();
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);

  useEffect(() => {
    const referer = searchParams.get('referer');
    const hasSeenPopup = localStorage.getItem(WELCOME_POPUP_KEY);

    if (referer === 'mashlom-main' && !hasSeenPopup) {
      setShowWelcomePopup(true);
    }
  }, [searchParams]);

  const handleCloseWelcomePopup = () => {
    localStorage.setItem(WELCOME_POPUP_KEY, 'true');
    setShowWelcomePopup(false);
  };

  const renderContent = () => {
    if (hospitalName && appName) {
      const isNursingView = appName === 'resus-nursing';
      const AppComponent = appMap[appName];

      if (AppComponent) {
        return <AppComponent hospital={hospitalName} isNursingView={isNursingView} />;
      }
      return <div>App not found</div>;
    }

    const userHasDepartments = hospitals?.some((h) => h.departments?.length > 0);

    if (authToken) {
      return userHasDepartments ? <LoggedInApps /> : <GuestApps />;
    }

    return <GuestApps />;
  };

  return (
    <>
      {/* Outside CommonLayout on purpose: the warning belongs directly under the
          fixed header, above the breadcrumb, and edge to edge. Gated on the app
          actually rendering: some rows in the App collection have no screen here
          (retired or unrouted keys), and a slug that shows "app not found" must
          not carry a warning about an app the visitor cannot see. */}
      {hospitalName && appName && appMap[appName] && <InDevelopmentBanner appToken={appName} />}
      <CommonLayout>
        <div className="apps-showcase">{renderContent()}</div>

        <Modal
          isOpen={showWelcomePopup}
          setIsOpen={setShowWelcomePopup}
          primaryButton={{
            text: 'אישור',
            onClick: handleCloseWelcomePopup,
          }}
        >
          <div style={{ textAlign: 'right', lineHeight: '1.8', fontSize: '1.2rem' }}>
            <p style={{ fontSize: '1.4rem', fontWeight: 'bold' }}>התחדשנו!</p>
            <p>
              בנינו פלטפורמה חדשה, מתוך תקווה שתוכלו למצוא במקום אחד את כל מה שאתם צריכים. אם עוד
              לא נרשמתם - אתם מוזמנים לעשות זאת (ניתן לעשות זאת מתוך התפריט).
            </p>
            <p>
              מתקשים? משהו לא ברור? יש משהו שאנחנו יכולים לעזור?
              <br />
              אנחנו במרחק אימייל אחד: mashlom.me@gmail.com
            </p>
            <p style={{ marginTop: '1.5rem', fontWeight: 'bold' }}>צוות משלום.</p>
          </div>
        </Modal>
      </CommonLayout>
    </>
  );
};

export default Apps;